/**
 * Question service for handling question-related operations
 */
import { apiRequest } from '../apiInterceptor';

/**
 * Gets the current question based on the index
 * @param {Array} questions - The array of questions
 * @param {number} currentQuestionIndex - The current question index
 * @param {Array} mockQuestions - Mock questions to use as fallback
 * @returns {Object} The current question
 */
export const getCurrentQuestion = (questions, currentQuestionIndex, mockQuestions) => {
  // console.log('[getCurrentQuestion] 📋 Called with:', {
  //   questionsLength: questions?.length || 0,
  //   currentQuestionIndex,
  //   mockQuestionsLength: mockQuestions?.length || 0,
  //   timestamp: new Date().toISOString()
  // });
  
  // If no questions are loaded yet, use mock data
  if (!questions || questions.length === 0) {
    // console.log('[getCurrentQuestion] ⚠️ No questions available, using mock data');
    return mockQuestions[currentQuestionIndex % mockQuestions.length];
  }
  
  // Check bounds
  if (currentQuestionIndex < 0 || currentQuestionIndex >= questions.length) {
    console.error('[getCurrentQuestion] ❌ Index out of bounds:', {
      currentQuestionIndex,
      questionsLength: questions.length
    });
    return null;
  }
  
  const question = questions[currentQuestionIndex];
  // console.log('[getCurrentQuestion] ✅ Returning question:', {
  //   index: currentQuestionIndex,
  //   questionId: question?.id || question?.questionId,
  //   questionText: question?.questionText?.substring(0, 50) + '...'
  // });
  
  // Return the current question
  return questions[currentQuestionIndex];
};

/**
 * Extracts clues from formatted text using brackets or <clue> tags
 * @param {string} formattedText - The formatted text containing clues
 * @returns {string[]} Array of extracted clues
 */
export const extractClues = (formattedText) => {
  if (!formattedText) return [];
  
  const clues = [];
  
  // Extract clues from [text] format
  const bracketRegex = /\[([^\]]+)\]/g;
  const bracketMatches = [...(formattedText.matchAll(bracketRegex) || [])];
  bracketMatches.forEach(match => clues.push(match[1]));
  
  // Extract clues from <clue>text</clue> format
  const tagRegex = /<clue>(.*?)<\/clue>/g;
  const tagMatches = [...(formattedText.matchAll(tagRegex) || [])];
  tagMatches.forEach(match => clues.push(match[1]));
  
  return clues;
};

/**
 * Gets the question with clues highlighted or hidden
 * @param {Object} question - The question object
 * @param {boolean} showClues - Whether to show clues
 * @returns {string} The question text with clues processed
 */
export const getQuestionWithClues = (question, showClues) => {
  if (!question) {
    return '';
  }
  
  // Different APIs might use different property names for the formatted question text
  let formattedText = null;
  
  // Try to get the question text from various possible properties in order of preference
  if (typeof question.questionFormatted === 'string' && question.questionFormatted.trim()) {
    formattedText = question.questionFormatted;
  } else if (typeof question.bodyFormatted === 'string' && question.bodyFormatted.trim()) {
    formattedText = question.bodyFormatted;
  } else if (typeof question.formatted === 'string' && question.formatted.trim()) {
    formattedText = question.formatted;
  } else if (typeof question.question === 'string' && question.question.trim()) {
    formattedText = question.question;
  } else if (typeof question.body === 'string' && question.body.trim()) {
    formattedText = question.body;
  } else if (typeof question.text === 'string' && question.text.trim()) {
    formattedText = question.text;
  }
  
  // Handle object-type questions with improved error handling
  if (!formattedText && typeof question === 'object') {
    // Check each common field that might contain the question
    const fields = ['questionFormatted', 'bodyFormatted', 'formatted', 'question', 'body', 'text'];
    for (const field of fields) {
      if (!formattedText && question[field]) {
        const processed = handlePotentialObjectText(question[field]);
        if (processed && processed !== '[object Object]' && processed !== 'Text content unavailable') {
          formattedText = processed;
          break;
        }
      }
    }
    
    // If none of the specific fields worked, try the entire question
    if (!formattedText) {
      formattedText = handlePotentialObjectText(question);
    }
  }
  
  // Final fallback check
  if (!formattedText || formattedText === '[object Object]') {
    console.warn('Potential issue with question format:', question);
    
    // Try more aggressively to get something readable
    if (typeof question === 'string') {
      formattedText = question;
    } else if (typeof question === 'object') {
      // One last attempt - look for any field that might contain useful text
      const allKeys = Object.keys(question);
      for (const key of allKeys) {
        if (typeof question[key] === 'string' && question[key].length > 10) {
          // This might be a question text
          formattedText = question[key];
          break;
        }
      }
      
      // If still nothing found, fall back to stringification
      if (!formattedText) {
        try {
          formattedText = JSON.stringify(question, null, 2);
          // If it's just an empty object, provide a more helpful message
          if (formattedText === '{}' || formattedText === '[]') {
            formattedText = 'Question content unavailable (empty object)';
          }
        } catch (e) {
          formattedText = 'Question content unavailable';
        }
      }
    } else {
      formattedText = 'Question content unavailable';
    }
  }
    // Process clues if enabled and the text contains them
  if (formattedText && typeof formattedText === 'string') {
    if (!showClues) {
      // Hide clues by replacing brackets or <clue> tags with just the text
      formattedText = formattedText.replace(/\[([^\]]+)\]/g, '$1'); // [text] format
      formattedText = formattedText.replace(/<clue>(.*?)<\/clue>/g, '$1'); // <clue>text</clue> format
    } else {
      // Highlight clues by applying styling to text within brackets or <clue> tags
      formattedText = formattedText.replace(/\[([^\]]+)\]/g, 
        '<span style="background-color: rgba(255, 220, 100, 0.3); padding: 0 4px; border-radius: 3px; font-weight: 600;">$1</span>');
      formattedText = formattedText.replace(/<clue>(.*?)<\/clue>/g, 
        '<span style="background-color: rgba(255, 220, 100, 0.3); padding: 0 4px; border-radius: 3px; font-weight: 600;">$1</span>');
    }
  }
  
  return formattedText;
};

/**
 * Helper function to handle potential object text values
 * @param {any} textField - The field that might be an object or string
 * @returns {string} A string representation of the field
 */
const handlePotentialObjectText = (textField) => {
  if (typeof textField === 'string') {
    return textField;
  } else if (typeof textField === 'object' && textField !== null) {
    try {
      // Check if the object has a meaningful text property we can use
      // Prioritize common text field names
      const textFieldNames = [
        'text', 'content', 'value', 'body', 'question', 'formatted', 
        'questionFormatted', 'bodyFormatted', 'description', 'label'
      ];
      
      // First, check for direct string properties
      for (const fieldName of textFieldNames) {
        if (textField[fieldName] && typeof textField[fieldName] === 'string' && textField[fieldName].trim()) {
          return textField[fieldName];
        }
      }
      
      // Then check for nested object properties that might have text
      for (const fieldName of textFieldNames) {
        const nestedField = textField[fieldName];
        if (nestedField && typeof nestedField === 'object' && nestedField !== null) {
          // Check if the nested object has any text properties
          for (const nestedFieldName of textFieldNames) {
            if (nestedField[nestedFieldName] && typeof nestedField[nestedFieldName] === 'string' && nestedField[nestedFieldName].trim()) {
              return nestedField[nestedFieldName];
            }
          }
        }
      }
      
      // Try to get a meaningful representation of the object
      if (textField.toString && typeof textField.toString === 'function' && textField.toString() !== '[object Object]') {
        return textField.toString();
      }
      
      // If it's likely to be a proper data object with meaningful content, stringify it
      const json = JSON.stringify(textField);
      if (json && json !== '{}' && json !== '[]') {
        // Check if this is just a wrapped string (like {"value":"Some text"})
        try {
          const parsed = JSON.parse(json);
          const keys = Object.keys(parsed);
          
          // If it's a single-key object with a string value, return the value
          if (keys.length === 1 && typeof parsed[keys[0]] === 'string') {
            return parsed[keys[0]];
          }
          
          // For multi-key objects, look for likely question text properties
          for (const fieldName of textFieldNames) {
            if (parsed[fieldName] && typeof parsed[fieldName] === 'string' && parsed[fieldName].trim()) {
              return parsed[fieldName];
            }
          }
          
          // Last resort: Find the longest string property in the object
          let longestString = '';
          for (const key of keys) {
            if (typeof parsed[key] === 'string' && parsed[key].length > longestString.length) {
              longestString = parsed[key];
            }
          }
          
          if (longestString) {
            return longestString;
          }
        } catch (e) {
          // Ignore parsing errors, just use the full JSON
        }
        
        // If we can't extract a meaningful string, use the full JSON but add formatting
        try {
          // Try to make the JSON more readable
          const formattedJson = JSON.parse(json);
          return JSON.stringify(formattedJson, null, 2);
        } catch (e) {
          // If parsing fails again, just return the raw JSON
          return json;
        }
      }
      
      return 'Text content unavailable';
    } catch (e) {
      console.error('Failed to stringify object text field:', e);
      return 'Text content unavailable';
    }
  } else if (textField === null || textField === undefined) {
    return '';
  } else {
    return String(textField);
  }
};

/**
 * Determine if selected answer is correct
 * @param {Object} question - The question object
 * @param {string} selectedAnswer - The selected answer letter
 * @returns {boolean} Whether the selected answer is correct
 */
export const isSelectedAnswerCorrect = (question, selectedAnswer) => {
  if (!question || !selectedAnswer) return false;
  
  // For questions with correctAnswer property (e.g., mock questions)
  if (question.correctAnswer) {
    return question.correctAnswer === selectedAnswer;
  }
  
  // For API questions with answers array containing isCorrect
  if (question.answers && Array.isArray(question.answers)) {
    const selectedAnswerIndex = selectedAnswer.charCodeAt(0) - 65; // Convert A->0, B->1, etc.
    if (selectedAnswerIndex >= 0 && selectedAnswerIndex < question.answers.length) {
      return !!question.answers[selectedAnswerIndex].isCorrect;
    }
  }
  
  return false;
};

/**
 * Gets the correct answer ID from the question
 * @param {Object} question - The question object
 * @returns {string|null} The correct answer ID or null if not found
 */
export const getCorrectAnswerId = (question) => {
  if (!question) return null;
  
  // For mock questions with correctAnswer property
  if (question.correctAnswer) {
    return question.correctAnswer;
  }
  
  // For API questions with answers array containing isCorrect
  if (question.answers && Array.isArray(question.answers)) {
    for (let i = 0; i < question.answers.length; i++) {
      if (question.answers[i].isCorrect) {
        return String.fromCharCode(65 + i); // Convert 0->A, 1->B, etc.
      }
    }
  }
  
  return null;
};

/**
 * Gets the explanation text from the question
 * @param {Object} question - The question object
 * @returns {string} The explanation text
 */
export const getExplanationText = (question) => {
  if (!question) return '';
  
  if (question.explanation) {
    return question.explanation;
  }
  
  if (question.explanationFormatted) {
    return question.explanationFormatted;
  }
  
  return '';
};

/**
 * Gets detailed information for a specific question by ID
 * This function fetches the complete question data including properly formatted
 * text, answer options, explanations, and clues from the backend API
 * 
 * @param {number} questionId - The ID of the question to fetch
 * @returns {Promise<Object>} - The detailed question object
 */
export const getQuestionDetails = async (questionId) => {
  if (!questionId) {
    console.error('Cannot fetch question details: No question ID provided');
    return null;
  }
    try {
    // console.log(`Fetching question details for ID: ${questionId}`);
    const response = await apiRequest(`/api/question/${questionId}`);
    
    if (!response) {
      console.warn(`No data returned from API for question ID: ${questionId}`);
      return null;
    }
    
    // console.log(`Successfully fetched details for question ID: ${questionId}`, response);
    
    // Process the response to ensure the question text is properly formatted
    if (response) {
      // Make sure answers array is structured properly
      if (response.answers && Array.isArray(response.answers)) {
        // Process each answer to ensure it has proper text
        response.answers = response.answers.map(answer => {
          if (typeof answer === 'string') {
            return { text: answer, answerFormatted: answer };
          } else if (typeof answer === 'object' && answer !== null) {
            // Ensure answer has a text property by checking various fields
            if (!answer.text) {
              if (answer.answerFormatted) {
                answer.text = answer.answerFormatted;
              } else if (answer.answer) {
                answer.text = answer.answer;
              } else if (answer.content) {
                answer.text = answer.content;
              } else if (answer.value) {
                answer.text = answer.value;
              }
            }
          }
          return answer;
        });
      }
    }
    
    return response;
  } catch (error) {
    console.error(`Error fetching question details for ID ${questionId}:`, error);
    return null;
  }
};

/**
 * Fetches question details from the API by ID, with proper caching
 * This is the recommended function to use when fetching individual question details
 * 
 * @param {number} questionId - The ID of the question to fetch
 * @returns {Promise<Object>} - The question details with all associated data
 */
export const fetchQuestionById = async (questionId) => {
  if (!questionId) {
    console.error('Cannot fetch question: No question ID provided');
    return null;
  }
  
  // console.log(`[fetchQuestionById] 🌐 Fetching question details for ID: ${questionId}`);
  // console.log(`[fetchQuestionById] 🌐 API URL will be: /api/question/${questionId}`);
  
  try {
    // console.log(`Fetching question ${questionId} from API...`);
    const questionData = await apiRequest(`/api/question/${questionId}`);
    
    if (!questionData) {
      console.warn(`[fetchQuestionById] ⚠️ No data returned from question API for ID: ${questionId}`);
      return null;
    }
    
    // console.log(`[fetchQuestionById] ✅ Successfully fetched question ${questionId}:`, {
    //   id: questionData.id,
    //   questionText: questionData.questionText?.substring(0, 50) + '...'
    // });
    
    // Post-process the question data to ensure consistent format
    return processQuestionData(questionData);
  } catch (error) {
    console.error(`Error fetching question ${questionId}:`, error);
    return null;
  }
};

/**
 * Process raw question data to ensure consistent format
 * @param {Object} data - Raw question data from API
 * @returns {Object} - Processed question data with consistent format
 */
const processQuestionData = (data) => {
  if (!data) return null;
  
  // Create a copy to avoid mutating the original
  const processed = { ...data };
  
  // Ensure question text is available and properly formatted
  if (!processed.bodyFormatted && processed.body) {
    processed.bodyFormatted = processed.body;
  }
  
  // Process answers if they exist
  if (processed.answers && Array.isArray(processed.answers)) {
    processed.answers = processed.answers.map(answer => {
      // If it's a string, convert to object format
      if (typeof answer === 'string') {
        return { 
          answerFormatted: answer,
          text: answer
        };
      }
      
      // If it's an object, ensure it has the required properties
      if (typeof answer === 'object' && answer !== null) {
        const processedAnswer = { ...answer };
        
        // Ensure text property exists
        if (!processedAnswer.text) {
          if (processedAnswer.answerFormatted) {
            processedAnswer.text = processedAnswer.answerFormatted;
          } else if (processedAnswer.answer) {
            processedAnswer.text = processedAnswer.answer;
          } else if (processedAnswer.content) {
            processedAnswer.text = processedAnswer.content;
          } else if (processedAnswer.value) {
            processedAnswer.text = processedAnswer.value;
          }
        }
        
        return processedAnswer;
      }
      
      return answer;
    });
  }
  
  return processed;
};

/**
 * Gets the question count based on filter criteria
 * @param {Object} filterRequest - The filter request object
 * @param {Object} services - The services object
 * @returns {Promise<number>} The count of questions matching the filter
 */
export const getQuestionsCount = async (filterRequest, services) => {
  try {
    return await services.exam.getQuestionsCount(filterRequest);
  } catch (error) {
    console.error('Error fetching question count:', error);
    return 0;
  }
};

/**
 * Validates if a question object has all required properties
 * @param {Object} question - The question object to validate
 * @returns {boolean} True if the question is valid, false otherwise
 */
export const validateQuestion = (question) => {
  if (!question) return false;
  
  // Basic validation for required properties
  const hasQuestion = !!question.question || !!question.questionFormatted;
  const hasAnswers = question.answers && Array.isArray(question.answers) && question.answers.length >= 2;
  
  return hasQuestion && hasAnswers;
};

/**
 * Helper function to fetch all question details for an exam session
 * @param {Object} sessionData - The exam session data
 * @returns {Promise<Array>} - Array of detailed question objects
 */
export const fetchQuestionDetailsForSession = async (sessionData) => {
  try {
    // Check if we're getting an empty or invalid response
    if (!sessionData) {
      console.error('Failed to fetch session data - response is empty');
      return [];
    }
    
    // console.log('Session data fetched:', sessionData);
    
    // Extract the question IDs from the session data
    let questionIds = [];
    
    // The session might have different data structures, so we need to handle multiple formats
    if (sessionData.examQuestions && Array.isArray(sessionData.examQuestions)) {
      // Format 1: session has an examQuestions array directly
      questionIds = sessionData.examQuestions
        .filter(q => q.questionId || q.id)
        .map(q => q.questionId || q.id);
    } else if (sessionData.questions && Array.isArray(sessionData.questions)) {
      // Format 2: session has a questions array directly
      questionIds = sessionData.questions
        .filter(q => q.questionId || q.id)
        .map(q => q.questionId || q.id);
    } else {
      // Format 3: session ID might be used as a property name in the response
      // Look for properties with examQuestions array
      for (const key in sessionData) {
        if (sessionData[key] && 
            typeof sessionData[key] === 'object' && 
            sessionData[key].examQuestions && 
            Array.isArray(sessionData[key].examQuestions)) {
          questionIds = sessionData[key].examQuestions
            .filter(q => q.questionId || q.id)
            .map(q => q.questionId || q.id);
          break;
        }
      }
    }
    
    // If we still don't have question IDs, just return the original data
    if (!questionIds.length) {
      // console.warn('No question IDs found in session data');
      return sessionData;
    }
    
    // console.log(`Found ${questionIds.length} question IDs to fetch details for`);
    
    // Now fetch the detailed question data for each ID
    const questionPromises = questionIds.map(id => 
      fetchQuestionById(id)
        .catch(err => {
          console.error(`Error fetching question ${id}:`, err);
          return null;
        })
    );
    
    // Wait for all promises to resolve
    const questions = await Promise.all(questionPromises);
    
    // Filter out any failed fetches
    const validQuestions = questions.filter(q => q !== null);
    
    // console.log(`Successfully fetched ${validQuestions.length} out of ${questionIds.length} questions`);
    
    return validQuestions;
  } catch (error) {
    console.error('Error in fetchQuestionDetailsForSession:', error);
    return [];
  }
}

// Export all functions
export default {
  getCurrentQuestion,
  extractClues,
  getQuestionWithClues,
  isSelectedAnswerCorrect,
  getCorrectAnswerId,
  getExplanationText,
  getQuestionsCount,
  validateQuestion,
  getQuestionDetails,
  fetchQuestionById,
  fetchQuestionDetailsForSession
};
